<script setup lang="ts">
import { computed, onMounted, ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";

import { eventActions } from "@/action/eventActions";
import { timerStarted } from "@/analytics/events/other";
import { trackEvent } from "@/analytics/track";
import { serverTime } from "@/backend/serverTime";
import IconButton from "@/components/ui/IconButton/IconButton.vue";
import { useEventBus } from "@/composables/useEventBus";
import { keepEndedTimer } from "@/model/Settings";
import type { ServerTime, TimerEvent, TimerState } from "@/model/timer";
import Duration from "@/utils/Duration";

import ActiveTimer from "./ActiveTimer.vue";
import InactiveTimer from "./InactiveTimer.vue";

const props = defineProps<{ timer: TimerEvent }>();

const name = ref(props.timer.data.name);
const duration = ref(Duration.fromTimer(props.timer.data, serverTime));

const { onServerTick } = useEventBus();

onMounted(() => {
  if (timerFinished()) {
    remove();
  } else {
    onServerTick(update);
  }
});

watchEffect(() => (name.value = props.timer.data.name));

const { t } = useI18n();
const stateInfo = computed(() =>
  isState("paused")
    ? ({ icon: "timer/play", label: t("label.timerPanel.resume"), next: "running" } as const)
    : ({ icon: "timer/pause", label: t("label.timerPanel.pause"), next: "paused" } as const),
);

function timerFinished() {
  return (
    isState("running") &&
    Duration.fromEnd(props.timer.data.end, serverTime).value < -keepEndedTimer / 1000
  );
}

function update(serverTime: ServerTime) {
  if (isState("running") && duration.value.value >= 0) {
    duration.value = Duration.fromEnd(props.timer.data.end, serverTime).nonNegative();
  }
}

function isState(s: TimerState) {
  return props.timer.data.state === s;
}

async function setState(s: TimerState) {
  if (!isState(s) && s === "running") {
    const scope = props.timer.artId ? "art" : "board";
    trackEvent(timerStarted(scope));
  }

  if (!isState(s)) {
    await eventActions.updateTimer("sidebar-menu", props.timer.id, {
      name: name.value,
      state: s,
      duration: duration.value.value,
      end: duration.value.end(serverTime),
    });
  }
}

function remove() {
  void eventActions.delete("sidebar-menu", props.timer.id);
}
</script>

<template>
  <div class="edit-timer">
    <InactiveTimer
      v-if="isState('stopped')"
      :name
      :duration
      @name="name = $event"
      @duration="duration = $event"
      @start="setState('running')"
      @cancel="remove"
    />
    <ActiveTimer v-else :name :duration :user="timer.updatedByUser!" :paused="isState('paused')">
      <!-- Play/Pause button (depending on state) -->
      <IconButton
        :icon="stateInfo.icon"
        :aria-label="stateInfo.label"
        @click="setState(stateInfo.next)"
      />
      <!-- Stop button -->
      <IconButton
        icon="timer/stop"
        :aria-label="$t('label.timerPanel.stop')"
        @click="setState('stopped')"
      />
    </ActiveTimer>
  </div>
</template>

<style lang="scss">
@use "@/styles/variables/a11y" as colors-a11y;
@use "@/styles/variables/colors";

.edit-timer {
  border-top: 1px solid colors-a11y.$divider-color;
  border-bottom: 1px solid colors-a11y.$divider-color;
  padding: 2em;
}
</style>
